// Source: maplecc.ca/leadership-team and official portraits supplied by MCC.
// `image` is the front (card) portrait; `profileImage` is shown on the profile page.
export type Leader = {
  slug: string;
  name: string;
  initials: string;
  role: string;
  /** One-line summary used on cards and previews. */
  summary: string;
  bio: string[];
  highlightsIntro?: string;
  highlights?: string[];
  closing?: string[];
  quote?: string;
  image?: string;
  profileImage?: string;
  linkedin?: string;
};

export const LEADERSHIP_INTRO =
  "The MCC academic team comprises individuals who have lived or taught in Canada or similar international education contexts, including a curriculum team that is 100% composed of Canadian-certified teachers and instructional coaches.";

export const PRINCIPAL: Leader = {
  slug: "tinuade-olufolabi",
  name: "Tinuade Olufolabi",
  initials: "TO",
  role: "Principal",
  summary: "An accomplished educator with over 25 years of experience in teaching and school leadership.",
  image: "https://cdn.magicpatterns.com/uploads/hL5W9Kvum9dDNfHqEVNCat/tinuade1.png",
  profileImage: "https://cdn.magicpatterns.com/uploads/9kBTSNRRNBn5m99MTg9jXC/tinuade2.jpg",
  linkedin: "https://www.linkedin.com/in/tinuade-olufolabi-a3473a168/",
  quote: "Every child can reach his or her potential.",
  bio: [
    "Mrs. Tinuade Olufunmilayo Olufolabi is an accomplished educator with over 25 years of experience in teaching and leading. She holds a Bachelor of Arts degree in French and a Postgraduate Diploma in Education. Within the span of twenty-five years, she has taught in Early Years, Primary, Secondary and Sixth Form sections and has been a high school principal.",
    "Her passion for education was born out of her love for children and her firm belief that every child can reach his or her potential.",
    "With excellent experience in programme initiative, staff development, promoting a safe environment and optimising learning, she holds a remarkable record for exceeding faculty goals through good external and internal communication and a strong commitment to maintaining a student-centred learning school.",
  ],
  highlightsIntro: "An energetic and warm leader with first-rate administrative and organisational abilities, she excels at:",
  highlights: [
    "Orienting new teachers to smooth acclimatisation.",
    "Resolving conflicts and improving student behaviour.",
    "Increasing test scores through tutoring.",
    "Developing new programmes to advance learning.",
  ],
  closing: [
    "She believes that by being a fine example, she can motivate others. She has devoted her energy to building confidence in students and ensuring that they do not settle for less than excellence.",
    "She is a recipient of numerous awards of excellence and has attended many workshops and seminars within and outside Nigeria.",
  ],
};

export const FOUNDER: Leader = {
  slug: "ebi-obaro",
  name: "Ebi Obaro",
  initials: "EO",
  role: "Founder / President",
  summary: "The brain behind Maple Education Canada Inc., who founded MCC in 2019.",
  image: "https://cdn.magicpatterns.com/uploads/h8pxcb6fnfwvoW8N46EWu1/ebiobaro1.jpg",
  profileImage: "https://cdn.magicpatterns.com/uploads/m4zcbbzH918PYtqeKkaUq4/ebiobaro2.jpg",
  linkedin: "https://www.linkedin.com/in/ebi-obaro-0025b350/",
  bio: [
    "Ebi Obaro is the brain behind Maple Education Canada Inc., the foremost provider of Canadian Immigration and Education Consulting Services in Nigeria. She is authorised to offer immigration advice and process visas to foreign nationals. Through her wealth of experience and careful selection and development of a team of passionate and astute professionals, she built Maple into a formidable company.",
    "With over 15 years’ experience of working with students and immigrants to Canada, she founded Maple Canadian College (MCC) in 2019 after careful evaluation of the experiences and needs of foreign nationals in Canada. The MCC programme was developed as a pre-university programme that bridges the gap between the Canadian high school curriculum and the West African high school syllabus, and prepares students for top global universities. As President of the college, she ensures that all school activities are carried out to the highest standards and conform to international best practice. She also presides over Worldwide Integrated Network and Education.",
    "Ebi is a certified Canadian Immigration and Education Consultant in good standing with the Immigration Consultants of Canada Regulatory Council (ICCRC) and the Canadian Association of Professional Immigration Consultants (CAPIC). When she is not improving her organisations’ offerings, she is spending time with her children and grandchildren.",
  ],
};

export const DIRECTORS: Leader[] = [
  {
    slug: "tukeni-obasi",
    name: "Tukeni Obasi",
    initials: "TO",
    role: "Director",
    summary: "Country Manager (Canada) for Maple Education Canada, with almost two decades in education consulting.",
    image: "https://cdn.magicpatterns.com/uploads/7NGRkcptoA7KsCntb3z67D/tukeniobasi1.jpg",
    profileImage: "https://cdn.magicpatterns.com/uploads/4tXFA9m886RdFyTnTQeoNs/tukeniobasi2.jpg",
    linkedin: "https://www.linkedin.com/in/tukeni-obasi-35445537/",
    bio: [
      "Tukeni Obasi is the Country Manager (Canada) for Maple Education Canada and an experienced Education and Immigration Consultant who is passionate about working with young students and professionals. For almost two decades, she has helped thousands of students secure admission to, and thrive in, Canadian institutions, and has mentored hundreds in their quest to become world-class professionals in Canada and other parts of the world.",
      "As the Canadian country manager for Maple Education, Tukeni is responsible for the affairs of the company in Canada. She oversees the business relationship between Maple Education and affiliate Canadian educational institutions, connecting students with life-changing education opportunities, and assists young professionals with work permits and permanent residence applications. Before joining Maple Education, Tukeni was a lawyer in private practice for 13 years, and an Investigation Officer at the Public Complaints Commission in Nigeria. She earned a Bachelor of Laws (LLB) from the University of Ife, a Barrister-at-Law (BL) from the Nigerian Law School, and an Immigration Consultant Diploma (IMCD) from Ashton College, BC, Canada.",
    ],
  },
  {
    slug: "panebi-oboh",
    name: "Panebi Oboh",
    initials: "PO",
    role: "Director",
    summary: "A Regulated Canadian Immigration Consultant and alumna of the Lagos Business School.",
    image: "https://cdn.magicpatterns.com/uploads/74hM7D7SmoUwrpwsDMn3Vn/Panebi-Oboh1.jpg",
    profileImage: "https://cdn.magicpatterns.com/uploads/74hM7D7SmoUwrpwsDMn3Vn/Panebi-Oboh1.jpg",
    bio: [
      "Panebi is a Regulated Canadian Immigration Consultant and embodies the best in that profession. A graduate of both the University of Lagos (Bachelor’s) and Lagos State University (Master of Business Administration), she is also an alumna of the Advanced Management Program of the Lagos Business School (LBS).",
      "She has a knack for people, combining emotional intelligence with a strong work ethic, leadership instinct and professional competence to guide clients toward their desired outcomes. Her contributions at work have always tended to surpass expectations.",
      "When Panebi is not working, she is probably reading a novel, leading a discussion group, at a community event, or helping someone in need.",
    ],
  },
  {
    slug: "mubo-olabode",
    name: "Mubo Olabode",
    initials: "MO",
    role: "Director",
    summary: "Leads MCC’s branding and university application initiatives. An OSSD holder herself.",
    image: "https://cdn.magicpatterns.com/uploads/1i7Gf8yuX9vFAJA6MNNTCC/Muboolabode1.jpg",
    profileImage: "https://cdn.magicpatterns.com/uploads/jwXb8XGweAAczARA6xURjy/muboolabode2.jpg",
    linkedin: "https://www.linkedin.com/in/mubo-olabode-78233280/",
    bio: [
      "Mubo leads the school’s branding and university application initiatives, while enhancing service delivery and exploring new opportunities for growth. She brings extensive experience working with newcomers to Canada, complemented by exceptional customer service skills developed during her tenure at the Royal Bank of Canada, one of the country’s top financial institutions.",
      "Recognised for her strong work ethic and dedication to excellence, Mubo consistently provides valuable insight in every professional role she assumes. She is a Certified Canadian Immigration Consultant, holds an Ontario Secondary School Diploma (OSSD), and earned a Bachelor’s degree in Sociology from Carleton University.",
    ],
  },
];

export const ALL_LEADERS: Leader[] = [PRINCIPAL, FOUNDER, ...DIRECTORS];
